import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface PackageTypeObject {
  id: string;
  nameAr: string;
  nameEn: string;
  slug: string;
}

export interface ClassCategoryObject {
  id: string;
  nameAr: string;
  nameEn: string;
  slug: string;
}

export interface PackageDiscountObject {
  id: string;
  packageTypeId: string;
  classCategoryId: string;
  discountAmount: number;
  numberOfLessons: number;
  createdAt: string;
  updatedAt: string;
  packageType: PackageTypeObject;
  classCategory: ClassCategoryObject;
  name: string;
}

export interface PackageDiscountListInfo {
  page: number;
  take: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  packageDiscount: Array<PackageDiscountObject>;
  errors: unknown;
}

export interface PackageDiscountFilterInfo {
  name: string;
}

@Module
export default class PackageDiscountModule
  extends VuexModule
  implements PackageDiscountListInfo
{
  packageDiscount = [] as Array<PackageDiscountObject>;
  allPackageDiscount = [] as Array<PackageDiscountObject>;
  allClassCategories = [] as Array<ClassCategoryObject>;
  packageDiscountFilter = {} as PackageDiscountFilterInfo;
  page = 1;
  take = 10;
  itemCount = 0;
  pageCount = 1;
  hasPreviousPage = false;
  hasNextPage = true;
  errors = {};

  /**
   * Get packageDiscount list
   */
  get packageDiscountList(): Array<PackageDiscountObject> {
    return this.packageDiscount;
  }

  get allPackageDiscountList(): Array<PackageDiscountObject> {
    return this.allPackageDiscount;
  }

  get allClassCategoriesList(): Array<ClassCategoryObject> {
    return this.allClassCategories;
  }

  get packageDiscountListCount(): number {
    return this.itemCount;
  }

  @Mutation
  [Mutations.SET_PACKAGE_DISCOUNT](data) {
    this.packageDiscount = data.data;
    this.page = data.meta.page;
    this.take = data.meta.take;
    this.itemCount = data.meta.itemCount;
    this.pageCount = data.meta.pageCount;
    this.hasPreviousPage = data.meta.hasPreviousPage;
    this.hasNextPage = data.meta.hasNextPage;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_ALL_PACKAGE_DISCOUNT](data) {
    this.allPackageDiscount = data.data;
  }

  @Mutation
  [Mutations.SET_ALL_CLASS_CATEGORIES](data) {
    this.allClassCategories = data.data;
  }

  @Mutation
  [Mutations.SET_PACKAGE_DISCOUNT_CURRENT_PAGE](page) {
    this.page = page;
  }

  @Mutation
  [Mutations.RESET_PACKAGE_DISCOUNT_LIST]() {
    this.packageDiscount = [] as Array<PackageDiscountObject>;
    this.page = 1;
    this.take = 10;
    this.itemCount = 0;
    this.pageCount = 1;
    this.hasPreviousPage = false;
    this.hasNextPage = true;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_PACKAGE_DISCOUNT_FILTER](obj) {
    this.packageDiscountFilter.name = obj.name;
  }

  @Action
  [Actions.RESET_PACKAGE_DISCOUNT_STORE]() {
    this.context.commit(Mutations.RESET_PACKAGE_DISCOUNT_LIST);
  }

  @Action
  [Actions.UPDATE_PACKAGE_DISCOUNT_FILTER](val) {
    this.context.commit(Mutations.SET_PACKAGE_DISCOUNT_FILTER, val);
  }

  @Action
  [Actions.CREATE_PACKAGE_DISCOUNT](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/finance/package-discount', data)
      .then((data) => {
        this.context.commit(Mutations.RESET_PACKAGE_DISCOUNT_LIST);
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPLOAD_PACKAGE_DISCOUNT_ATTACHMENT]({ id, data }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();
    return ApiService.post(`/package-discount/attachments/${id}`, data)
      .then(() => {
        this.context.commit(Mutations.RESET_PACKAGE_DISCOUNT_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.DELETE_PACKAGE_DISCOUNT](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/finance/package-discount', id)
      .then(() => {
        this.context.commit(Mutations.RESET_PACKAGE_DISCOUNT_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPDATE_PACKAGE_DISCOUNT]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('/finance/package-discount', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_PACKAGE_DISCOUNT_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPDATE_PACKAGE_DISCOUNT_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_PACKAGE_DISCOUNT_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_PACKAGE_DISCOUNT](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/finance/package-discount', id);
  }

  @Action
  [Actions.GET_PACKAGE_DISCOUNTS]() {
    const params = {
      page: this.page,
      name: this.packageDiscountFilter.name,
    };
    ApiService.setAuthorizationHeader();
    return ApiService.query('/finance/package-discount', { params })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_PACKAGE_DISCOUNT, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.GET_ALL_PACKAGE_DISCOUNTS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get(`/package-discount/all`)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ALL_PACKAGE_DISCOUNT, data);
        return data.data;
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.GET_ALL_CLASS_CATEGORIES]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/settings/class-category/all')
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ALL_CLASS_CATEGORIES, data);
        return data.data;
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }
}
